import React, { useEffect, useState } from "react";
import { Country, State, City, ICountry, IState, ICity } from "country-state-city";
import axios from "axios";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { Label } from "../ui/label";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { Input } from "../ui/input";
import { GOOGLE_MAPS_KEY } from "@/config";
import { useCustomLoader } from "../Loader";
import { cn } from "@/lib/utils";

type Props = {
    control: Control<any>;
    errors: FieldErrors;
    setValue: any;
    toast: any
    setError: any
    getValues: any
};

export const LocationSelector: React.FC<Props> = ({ control, errors, setValue, setError, toast, getValues }) => {
    const loader = useCustomLoader();
    const [pinCode, setPinCode] = useState<string>("");
    const [countries, setCountries] = useState<ICountry[]>([]);
    const [states, setStates] = useState<IState[]>([]);
    const [cities, setCities] = useState<ICity[]>([]);
    

    useEffect(() => {
        setCountries(Country.getAllCountries());
    }, []);

    useEffect(()=> {
        setPinCode(getValues("pincode"));
    },[getValues("pincode")])

    const handlePincodeSearch = (pincode: string) => {
        const timer = setTimeout(async() => {
                try {
                    loader(true);
                    // Example using Google Maps Geocoding API
                    const res = await axios.get(
                        `https://maps.googleapis.com/maps/api/geocode/json?address=${pincode}&key=${GOOGLE_MAPS_KEY}`
                    );
                    const result = res.data.results[0];
                    if (!result) return;
        
                    const components = result.address_components;
                    const countryName = components.find((c: any) => c.types.includes("country"))?.long_name;
                    const stateName = components.find((c: any) => c.types.includes("administrative_area_level_1"))?.long_name;
                    const cityName = components.find((c: any) => c.types.includes("locality"))?.long_name;
        
                    const country = countries.find((c) => c.name === countryName);
                    const state = country ? State.getStatesOfCountry(country.isoCode).find((s) => s.name === stateName) : undefined;
                    const city = (country && state)
                        ? City.getCitiesOfState(country.isoCode, state.isoCode).find((ct) => ct.name === cityName)
                        : undefined;
        
                    setStates(country ? State.getStatesOfCountry(country.isoCode) : []);
                    setCities(country && state ? City.getCitiesOfState(country.isoCode, state.isoCode) : []);
        
                    setTimeout(() => {
                        setValue("country", country?.name);
                        setValue("state", state?.name);
                        setValue("city", city?.name);
        
                        if (country?.name) setError("country", { message: "" });
                        if (state?.name) setError("state", { message: "" });
                        if (city?.name) setError("city", { message: "" });
                    }, 1000);
        
                    // onChange({ country, state, city, pincode });
                } catch (err) {
                    toast({
                        variant: "destructive",
                        title: "Error!",
                        description: "Pincode not found",
                    })
                }
                finally {
                    loader(false);
                }
        }, 600);

        return () => clearTimeout(timer);
    }

    useEffect(() => {
        const timer =  setTimeout(() => {
            if(pinCode){
                handlePincodeSearch(pinCode);
            }
        }, 500);
        return () => clearTimeout(timer);
    },[pinCode])

    return (
        <>
            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                <Controller
                    name="pincode"
                    control={control}

                    render={({ field }) => (
                        <>
                            <Label htmlFor="pincode">Pincode</Label>
                            <Input
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    setPinCode(e.target.value);
                                }}
                                placeholder="Please enter pincode"
                                id="pincode"
                            />
                        </>
                    )}
                />
                {
                    errors.pincode && (
                        <Typography sx={{ color: "red" }}>{String(errors.pincode.message)}</Typography>
                    )
                }

            </Grid>
            {/* Country Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Label htmlFor="country">Country</Label>
                            <Autocomplete
                                options={countries}
                                getOptionLabel={(option) => option.name}
                                value={countries.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                    setStates(newValue ? State.getStatesOfCountry(newValue.isoCode) : []);
                                    setCities([]);
                                }}
                                renderInput={(params) => (
                                    <div ref={params.InputProps.ref}>
                                        <TextField
                                            {...params}
                                            placeholder="Select Country"
                                            id="country"
                                            size="small"
                                            className={cn(
                                                "flex h-10 w-full rounded-md border-2  bg-background px-3 py-2 text-base ring-offset-background file:border file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",

                                            )}
                                        />
                                    </div>
                                )}
                            />
                            {errors.country && (
                                <Typography sx={{ color: "red" }}>
                                    {String(errors.country.message)}
                                </Typography>
                            )}
                        </>
                    )}
                />
            </Grid>


            {/* State Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Label htmlFor="state">State</Label>
                            <Autocomplete
                                options={states}
                                getOptionLabel={(option) => option.name}
                                value={states.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                    setCities(newValue ? City.getCitiesOfState(newValue.countryCode, newValue.isoCode) : []);
                                }}
                                renderInput={(params) => (
                                    <div ref={params.InputProps.ref}>
                                        <TextField
                                            {...params}
                                            placeholder="Select State"
                                            id="state"
                                            size="small"
                                            className={cn(
                                                "flex h-10 w-full rounded-md border-2  bg-background px-3 py-2 text-base ring-offset-background file:border file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",

                                            )}
                                        />
                                    </div>
                                )}
                            />
                            {errors.state && (
                                <Typography sx={{ color: "red" }}>{String(errors.state.message)}</Typography>
                            )}
                        </>
                    )}
                />
            </Grid>

            {/* City Select */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Label htmlFor="city">City</Label>
                            {/* <Select
                                value={field.value || ""}
                                onValueChange={(val) => {
                                    const city = cities.find((ct) => ct.name === val);
                                    field.onChange(city?.name);
                                }}
                            >
                                <SelectTrigger className="text-gray-900 text-sm" id="city">
                                    <SelectValue placeholder="Select City" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cities.map((ct) => (
                                        <SelectItem key={ct.name} value={ct.name}>
                                            {ct.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select> */}
                            <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.name}
                                value={cities.find((c) => c.name === field.value) || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue ? newValue.name : "");
                                }}
                                renderInput={(params) => (
                                    <div ref={params.InputProps.ref}>
                                        <TextField
                                            {...params}
                                            placeholder="Select City"
                                            id="city"
                                            size="small"
                                            className={cn(
                                                "flex h-10 w-full rounded-md border-2  bg-background px-3 py-2 text-base ring-offset-background file:border file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",

                                            )}
                                        />
                                    </div>
                                )}
                            />
                            {errors.city && (
                                <Typography sx={{ color: "red" }}>{String(errors.city.message)}</Typography>
                            )}
                        </>
                    )}
                />
            </Grid>
        </>
    );
};
